import React, { useContext, useState, useEffect } from 'react';
import _ from 'underscore';
import PropTypes from 'prop-types';
import { Editor, GetContentMode } from 'roosterjs';
import classNames from 'classnames';

import { Label } from '@fluentui/react/lib/Label';
import { mobileDevice } from '@bingads-webui-campaign-react/mca-core';
import {
  SiteConfigContext,
  useDebouncedDispatchSiteConfig,
} from '@bingads-webui-campaign-react/smart-page-context';
import {
  getDataSourceByKey,
  useDefaultEditorStyle,
} from '@bingads-webui-campaign-react/smart-component-util';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { RibbonButtons, RichTextEditor } from '@bingads-webui-react/rich-text-editor';
import { withErrorView } from '@bingads-webui-campaign-react/smart-page-error-boundary';
import { validateText, TextErrorMap } from './text-helper';

// Create a dangling div to calculate content length
const tmpDiv = document.createElement('div');
const editorForCharCount = new Editor(tmpDiv);

const ribbonButtons = [
  RibbonButtons.Bold,
  RibbonButtons.Italic,
  RibbonButtons.Underline,
  RibbonButtons.Bullet,
  RibbonButtons.Numbering,
  RibbonButtons.AlignLeft,
  RibbonButtons.AlignCenter,
  RibbonButtons.AlignRight,
  RibbonButtons.InsertHyperlink,
  RibbonButtons.RemoveHyperlink,
  RibbonButtons.Undo,
  RibbonButtons.Redo,
  RibbonButtons.RemoveFormatting,
  RibbonButtons.Clear,
];

export const getRawTextFromRichText = (richText) => {
  editorForCharCount.setContent(richText);

  return editorForCharCount.getContent(GetContentMode.PlainText);
};

const getStyles = () => ({
  charRemaining: {
    color: '#605e5c',
    fontSize: '12px',
    display: 'block',
    textAlign: 'right',
  },
  errorMessage: {
    color: '#a80000',
    fontSize: '12px',
    paddingTop: '5px',
  },
  rteContainer: {
    [mobileDevice]: {
      '& .rich-text-editor-wrapper': {
        minHeight: 180,
      },
    },
  },
});

const sectionTitleLabelId = 'smart-rich-text-editor-label';

const UnstyledSmartRichTextEditor = (props) => {
  const {
    // below two fields are mutually exclusive (dataSourceName takes precedence)
    dataSourceName, value,

    classes,
    className,
    label,
    onChange,
    maxCharCount,
    placeholder,
    customErrorMap,
    isRequired,
    onValidate,
  } = props;
  const { sectionTitleClassName } = useDefaultEditorStyle();
  const [validatedResult, setValidatedResult] = useState({});

  const { siteConfig } = useContext(SiteConfigContext);
  const inputVal = getDataSourceByKey(siteConfig, dataSourceName, value, false) || '';
  const debouncedDispatchSiteConfig = useDebouncedDispatchSiteConfig();
  const i18n = useContext(I18nContext);

  const onChangeAction = (val) => {
    if (dataSourceName) {
      debouncedDispatchSiteConfig({
        type: 'UPDATE_DATASOURCE_BY_NAME',
        payload: {
          name: dataSourceName,
          value: val,
        },
      });
    }

    onChange({
      value: val,
    });
  };

  useEffect(() => {
    const plainText = getRawTextFromRichText(inputVal);
    const validation = validateText(
      plainText,
      maxCharCount,
      i18n,
      undefined,
      isRequired,
      customErrorMap
    );

    onValidate(validation.errorMessage);
    setValidatedResult(validation);
  }, [customErrorMap, i18n, isRequired, inputVal, maxCharCount, onValidate]);

  return (
    <div className={className}>
      <Label id={sectionTitleLabelId} className={classNames(sectionTitleClassName, 'smart-rich-text-editor-label')} >
        <span>{label}</span>
      </Label>
      <div className={classNames('smart-rich-text-editor-container', classes.rteContainer)}>
        <RichTextEditor
          i18n={i18n}
          onContentChanged={onChangeAction}
          initialContent={inputVal}
          placeholder={placeholder}
          ribbonButtons={ribbonButtons}
        />
      </div>
      <span className={classes.charRemaining}>{validatedResult.charRemainingDescription}</span>
      <div role="alert">
        <span className={classes.errorMessage}>{validatedResult.errorMessage}</span>
      </div>
    </div>
  );
};

UnstyledSmartRichTextEditor.propTypes = {
  classes: PropTypes.shape({}).isRequired,
  className: PropTypes.string,
  label: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.node,
  ]).isRequired,
  isRequired: PropTypes.bool,
  onChange: PropTypes.func,
  maxCharCount: PropTypes.number,
  dataSourceName: PropTypes.string,
  value: PropTypes.string,
  placeholder: PropTypes.string,
  onValidate: PropTypes.func,
  customErrorMap: PropTypes.shape({
    [TextErrorMap.MaxLength]: PropTypes.string,
  }),
};

const emptyObj = {};

UnstyledSmartRichTextEditor.defaultProps = {
  className: null,
  onChange: _.noop,
  maxCharCount: undefined,
  placeholder: undefined,

  // below two fields are mutually exclusive (dataSourceName takes precedence)
  dataSourceName: null,
  value: undefined,
  customErrorMap: emptyObj,
  isRequired: false,
  onValidate: _.noop,
};

export const SmartRichTextEditor = withErrorView(withDefaultStyles(
  UnstyledSmartRichTextEditor,
  getStyles
), 'componentMode');
